import Phaser from "phaser";

import {
  layoutBreadcrumbMessage,
  type BreadcrumbMessageLayoutOptions,
} from "./BreadcrumbMessageLayout";
import {
  SYMBOL_SEQUENCE_FONT_KEY,
  SYMBOL_SEQUENCE_FONT_SIZE,
  type BitmapFontLayoutProfile,
} from "./SymbolSequenceFont";

const DEFAULT_MAX_WIDTH_INSET = 16;
type VerticalAlign = "top" | "middle";

export interface LayoutBitmapTextRendererOptions {
  readonly depth: number;
  readonly tint?: number;
  readonly maxWidthInset?: number;
  readonly scale?: number;
  readonly fontProfile?: BitmapFontLayoutProfile;
  readonly asciiAdvance?: number;
  readonly spaceAdvance?: number;
  readonly fullAdvance?: number;
  readonly lineHeight?: number;
  readonly renderableChars?: string | readonly string[];
  readonly fallbackChar?: string;
  readonly fontKey?: string;
  readonly fontSize?: number;
  readonly additionalRenderableChars?: readonly string[];
}

export interface LayoutBitmapTextPlacement {
  readonly centerX?: number;
  readonly leftX?: number;
  readonly y: number;
  readonly verticalAlign?: VerticalAlign;
  readonly maxWidth?: number;
}

export interface LayoutBitmapTextBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export class LayoutBitmapTextRenderer {
  private readonly scene: Phaser.Scene;
  private readonly glyphPool: Phaser.GameObjects.BitmapText[] = [];
  private readonly viewportWidth: number;
  private readonly depth: number;
  private readonly maxWidthInset: number;
  private readonly scale: number;
  private fontKey: string;
  private fontSize: number;
  private layoutOptions: Omit<BreadcrumbMessageLayoutOptions, "maxWidth">;
  private visible: boolean = true;
  private alpha: number = 1;
  private tint: number;
  private activeGlyphCount: number = 0;
  private lastBounds: LayoutBitmapTextBounds | null = null;

  public constructor(scene: Phaser.Scene, options: LayoutBitmapTextRendererOptions) {
    this.scene = scene;
    this.viewportWidth = scene.cameras.main.width;
    this.depth = options.depth;
    this.maxWidthInset = options.maxWidthInset ?? DEFAULT_MAX_WIDTH_INSET;
    this.scale = options.scale ?? 1;
    const fontProfile = options.fontProfile;
    this.fontKey = options.fontKey ?? fontProfile?.bitmapFontKey ?? SYMBOL_SEQUENCE_FONT_KEY;
    this.fontSize = options.fontSize ?? fontProfile?.fontSize ?? SYMBOL_SEQUENCE_FONT_SIZE;
    this.tint = options.tint ?? 0xffffff;
    const asciiAdvance = options.asciiAdvance ?? fontProfile?.asciiAdvance;
    const spaceAdvance = options.spaceAdvance ?? fontProfile?.spaceAdvance;
    const fullAdvance = options.fullAdvance ?? fontProfile?.fullAdvance;
    const lineHeight = options.lineHeight ?? fontProfile?.lineHeight;
    const renderableChars = options.renderableChars ?? fontProfile?.renderableChars;
    const fallbackChar = options.fallbackChar ?? fontProfile?.fallbackChar;
    this.layoutOptions = {
      ...(asciiAdvance !== undefined ? { asciiAdvance } : {}),
      ...(spaceAdvance !== undefined ? { spaceAdvance } : {}),
      ...(fullAdvance !== undefined ? { fullAdvance } : {}),
      ...(lineHeight !== undefined ? { lineHeight } : {}),
      ...(renderableChars !== undefined ? { renderableChars } : {}),
      ...(fallbackChar !== undefined ? { fallbackChar } : {}),
      ...(options.additionalRenderableChars !== undefined
        ? { additionalRenderableChars: options.additionalRenderableChars }
        : {}),
    } satisfies Omit<BreadcrumbMessageLayoutOptions, "maxWidth">;
  }

  public setFontProfile(profile: BitmapFontLayoutProfile): void {
    const nextLayoutOptions = {
      asciiAdvance: profile.asciiAdvance,
      spaceAdvance: profile.spaceAdvance,
      fullAdvance: profile.fullAdvance,
      lineHeight: profile.lineHeight,
      renderableChars: profile.renderableChars,
      fallbackChar: profile.fallbackChar,
    } satisfies Omit<BreadcrumbMessageLayoutOptions, "maxWidth">;

    const profileUnchanged =
      this.fontKey === profile.bitmapFontKey
      && this.fontSize === profile.fontSize
      && this.layoutOptions.asciiAdvance === nextLayoutOptions.asciiAdvance
      && this.layoutOptions.spaceAdvance === nextLayoutOptions.spaceAdvance
      && this.layoutOptions.fullAdvance === nextLayoutOptions.fullAdvance
      && this.layoutOptions.lineHeight === nextLayoutOptions.lineHeight
      && this.layoutOptions.renderableChars === nextLayoutOptions.renderableChars
      && this.layoutOptions.fallbackChar === nextLayoutOptions.fallbackChar;

    if (profileUnchanged) {
      return;
    }

    this.fontKey = profile.bitmapFontKey;
    this.fontSize = profile.fontSize;
    this.layoutOptions = {
      ...this.layoutOptions,
      ...nextLayoutOptions,
    };
    this.resetGlyphPool();
  }

  public render(text: string, placement: LayoutBitmapTextPlacement): void {
    if (text.length === 0) {
      this.clear();
      return;
    }

    const maxWidth = placement.maxWidth ?? (this.viewportWidth - this.maxWidthInset);
    const layout = layoutBreadcrumbMessage(text, {
      ...this.layoutOptions,
      maxWidth,
    });
    const scaledWidth = layout.width * this.scale;
    const scaledHeight = layout.height * this.scale;
    const centerX =
      placement.leftX !== undefined
        ? placement.leftX + scaledWidth / 2
        : (placement.centerX ?? (this.viewportWidth / 2));
    const verticalAlign = placement.verticalAlign ?? "top";
    const blockTop =
      verticalAlign === "middle"
        ? Math.floor(placement.y - scaledHeight / 2)
        : Math.floor(placement.y);
    const blockLeft =
      placement.leftX !== undefined
        ? Math.floor(placement.leftX)
        : Math.floor(centerX - scaledWidth / 2);

    let glyphIndex = 0;
    for (const line of layout.lines) {
      const scaledLineWidth = line.width * this.scale;
      const lineLeft =
        placement.leftX !== undefined
          ? blockLeft
          : Math.floor(centerX - scaledLineWidth / 2);
      for (const glyph of line.glyphs) {
        const glyphText = this.getGlyph(glyphIndex);
        glyphText.setText(glyph.char);
        glyphText.setRotation(0);
        glyphText.setPosition(
          lineLeft + glyph.x * this.scale,
          blockTop + glyph.y * this.scale,
        );
        glyphText.setVisible(this.visible);
        glyphText.setAlpha(this.alpha);
        glyphText.setTint(this.tint);
        glyphIndex += 1;
      }
    }

    this.activeGlyphCount = glyphIndex;
    this.lastBounds = {
      x: blockLeft,
      y: blockTop,
      width: scaledWidth,
      height: scaledHeight,
    };
    this.hideUnusedGlyphs();
  }

  public getLastBounds(): LayoutBitmapTextBounds | null {
    return this.lastBounds;
  }

  public setVisible(visible: boolean): void {
    this.visible = visible;
    for (let index = 0; index < this.glyphPool.length; index += 1) {
      const glyph = this.glyphPool[index];
      if (glyph === undefined) {
        continue;
      }
      glyph.setVisible(visible && index < this.activeGlyphCount);
    }
  }

  public setAlpha(alpha: number): void {
    this.alpha = alpha;
    for (let index = 0; index < this.glyphPool.length; index += 1) {
      const glyph = this.glyphPool[index];
      if (glyph === undefined) {
        continue;
      }
      glyph.setAlpha(index < this.activeGlyphCount ? alpha : 0);
    }
  }

  public setTint(tint: number): void {
    this.tint = tint;
    for (const glyph of this.glyphPool) {
      glyph.setTint(tint);
    }
  }

  public clear(): void {
    this.activeGlyphCount = 0;
    this.lastBounds = null;
    for (const glyph of this.glyphPool) {
      glyph.setVisible(false);
      glyph.setAlpha(0);
      glyph.setRotation(0);
      glyph.setText("");
    }
  }

  public destroy(): void {
    this.resetGlyphPool();
  }

  private resetGlyphPool(): void {
    for (const glyph of this.glyphPool) {
      glyph.destroy();
    }
    this.glyphPool.length = 0;
    this.activeGlyphCount = 0;
    this.lastBounds = null;
  }

  private getGlyph(index: number): Phaser.GameObjects.BitmapText {
    const existing = this.glyphPool[index];
    if (existing !== undefined) {
      return existing;
    }

    const glyph = this.scene.add.bitmapText(
      0,
      0,
      this.fontKey,
      "",
      this.fontSize,
    );
    glyph.setOrigin(0, 0);
    glyph.setScale(this.scale);
    glyph.setScrollFactor(0);
    glyph.setDepth(this.depth);
    glyph.setTint(this.tint);
    glyph.setVisible(false);
    glyph.setAlpha(0);
    this.glyphPool[index] = glyph;
    return glyph;
  }

  private hideUnusedGlyphs(): void {
    for (let index = this.activeGlyphCount; index < this.glyphPool.length; index += 1) {
      const glyph = this.glyphPool[index];
      if (glyph === undefined) {
        continue;
      }
      glyph.setVisible(false);
      glyph.setAlpha(0);
      glyph.setRotation(0);
      glyph.setText("");
    }
  }
}
